import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { ChatConfirmationMode } from '@/api';
import { isEnumValue } from '@/utils/enumUtils';
import { useOrganizationStore } from '@/stores/organizationStore';

/**
 * Which chat tools pause on a user confirmation. Sent with every stream/resume
 * request rather than persisted on the thread (see `ChatConfirmationMode`), so
 * it is a pure client-side preference — remembered per org, like the chat model
 * in `useModelSelection.ts`.
 *
 * Deliberately icon- and color-free: each call site draws the modes with its
 * own glyphs from `components/design/icons` and tints them with its own theme
 * vars or Gaia tokens. Only the modes and their copy are shared.
 */
export interface ConfirmationModeOption {
  mode: ChatConfirmationMode;
  label: string;
  /** Hint under the segmented control — what this mode does and doesn't pause on. */
  description: string;
}

export const CONFIRMATION_MODE_DEFAULT = ChatConfirmationMode.READ;

export const CONFIRMATION_MODE_OPTIONS: ConfirmationModeOption[] = [
  {
    // Shown as "Manual" — the wire value stays `read` (ChatConfirmationMode.READ),
    // which is what the backend and the stored preference both key on.
    mode: ChatConfirmationMode.READ,
    label: 'Manual',
    description: 'Only actions that change data pause for approval.',
  },
  {
    mode: ChatConfirmationMode.AUTO,
    label: 'Auto',
    description: 'Nothing pauses, every tool runs immediately.',
  },
];

const CHAT_MODE_STORAGE_PREFIX = 'gaia:chat-mode:';

function chatModeStorageKey(orgUid: string): string {
  return `${CHAT_MODE_STORAGE_PREFIX}${orgUid}`;
}

export function readStoredConfirmationMode(
  orgUid: string | undefined
): ChatConfirmationMode | null {
  if (!orgUid) return null;
  try {
    const raw = localStorage.getItem(chatModeStorageKey(orgUid));
    if (isEnumValue(ChatConfirmationMode, raw)) {
      return raw;
    }
  } catch {
    // Ignore malformed/unavailable storage; fall back to the default.
  }
  return null;
}

export function storeConfirmationMode(
  orgUid: string | undefined,
  mode: ChatConfirmationMode
): void {
  if (!orgUid) return;
  try {
    localStorage.setItem(chatModeStorageKey(orgUid), mode);
  } catch {
    // Storage may be unavailable (private mode / quota); selection still works for the session.
  }
}

export function findConfirmationModeOption(
  mode: ChatConfirmationMode
): ConfirmationModeOption | undefined {
  return CONFIRMATION_MODE_OPTIONS.find(option => option.mode === mode);
}

export interface UseConfirmationModeReturn {
  currentConfirmationMode: Ref<ChatConfirmationMode>;
  confirmationModeLabel: ComputedRef<string>;
  confirmationModeDescription: ComputedRef<string>;
  selectConfirmationMode: (mode: ChatConfirmationMode) => void;
}

/**
 * Shared across callers for the same reason the chat model is (see
 * `useModelSelection.ts`): the picker lives in GaiaChatSettings, the mode is
 * sent by GaiaChat. Initialised on first use rather than at module load —
 * the storage key is per-org, so it needs the organization store.
 */
const currentConfirmationMode = ref<ChatConfirmationMode>(CONFIRMATION_MODE_DEFAULT);
let hydratedFromStorage = false;

export function useConfirmationMode(): UseConfirmationModeReturn {
  const organizationStore = useOrganizationStore();

  if (!hydratedFromStorage) {
    hydratedFromStorage = true;
    currentConfirmationMode.value =
      readStoredConfirmationMode(organizationStore.activeOrganization?.uid) ??
      CONFIRMATION_MODE_DEFAULT;
  }

  const confirmationModeLabel = computed(
    () => findConfirmationModeOption(currentConfirmationMode.value)?.label ?? 'Manual'
  );

  const confirmationModeDescription = computed(
    () => findConfirmationModeOption(currentConfirmationMode.value)?.description ?? ''
  );

  // Nothing to await or block on — the mode rides along with the next request.
  // No toast either: the pill the user just clicked already shows the new mode.
  function selectConfirmationMode(mode: ChatConfirmationMode): void {
    if (mode === currentConfirmationMode.value) return;
    currentConfirmationMode.value = mode;
    storeConfirmationMode(organizationStore.activeOrganization?.uid, mode);
  }

  watch(
    () => organizationStore.activeOrganization?.uid,
    orgUid => {
      const stored = readStoredConfirmationMode(orgUid);
      if (stored) currentConfirmationMode.value = stored;
    }
  );

  return {
    currentConfirmationMode,
    confirmationModeLabel,
    confirmationModeDescription,
    selectConfirmationMode,
  };
}
